import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign, faArrowLeft, faSave, faEdit, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';

interface PricingConfig {
  id: number;
  period_days: number;
  price_cents: number;
  currency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AdminPricing: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [pricing, setPricing] = useState<PricingConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<{ [key: number]: boolean }>({});
  const [editing, setEditing] = useState<{ [key: number]: boolean }>({});
  const [editValues, setEditValues] = useState<{ [key: number]: { price_cents: number; currency: string; is_active: boolean } }>({});

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await api.get('/subscriptions/admin/pricing');
      setPricing(response.data.pricing || []);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
      alert('Failed to load pricing configuration. You may not have admin access.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config: PricingConfig) => {
    setEditing({ ...editing, [config.id]: true });
    setEditValues({
      ...editValues,
      [config.id]: {
        price_cents: config.price_cents,
        currency: config.currency,
        is_active: config.is_active
      }
    });
  };

  const handleCancel = (id: number) => {
    const newEditing = { ...editing };
    delete newEditing[id];
    setEditing(newEditing);
    
    const newEditValues = { ...editValues };
    delete newEditValues[id];
    setEditValues(newEditValues);
  };

  const handleSave = async (config: PricingConfig) => {
    setSaving({ ...saving, [config.id]: true });
    
    try {
      const values = editValues[config.id];
      await api.put(`/subscriptions/admin/pricing/${config.period_days}`, {
        price_cents: values.price_cents,
        currency: values.currency,
        is_active: values.is_active
      });
      
      // Refresh pricing
      await fetchPricing();
      
      const newEditing = { ...editing };
      delete newEditing[config.id];
      setEditing(newEditing);
      
      const newEditValues = { ...editValues };
      delete newEditValues[config.id];
      setEditValues(newEditValues);
    } catch (error) {
      console.error('Failed to update pricing:', error);
      alert('Failed to update pricing configuration');
    } finally {
      setSaving({ ...saving, [config.id]: false });
    }
  };

  const getPeriodLabel = (days: number) => {
    if (days === 365) return '1 Year';
    return `${days} Days`;
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div className="text-center">
          <div className="gp-spinner mb-3" aria-hidden="true" />
          <p className="text-sm gp-text-muted">Loading pricing configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gp-page py-6">
      <div className="gp-container">
        <Link to="/dashboard" className="gp-back-link mb-4">
          <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
          {t('common.backToDashboard')}
        </Link>

        <div className="gp-page-header">
          <div className="flex items-center gap-2 mb-1">
            <span className="gp-icon-tile">
              <FontAwesomeIcon icon={faDollarSign} aria-hidden="true" />
            </span>
            <h1 className="gp-page-title">Pricing Configuration</h1>
          </div>
          <p className="gp-page-subtitle">
            Manage subscription plan prices
          </p>
        </div>

        <div className="gp-card p-0 overflow-hidden">
          <div className="gp-table-wrap border-0 rounded-none">
            <table>
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Price (Cents)</th>
                  <th>Price (USD)</th>
                  <th>Currency</th>
                  <th>Status</th>
                  <th className="!text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pricing.map((config) => {
                  const isEditing = editing[config.id];
                  const values = editValues[config.id] || config;
                  
                  return (
                    <tr key={config.id}>
                      <td>
                        <span className="font-semibold text-sm" style={{ color: 'var(--gp-ink)' }}>
                          {getPeriodLabel(config.period_days)}
                        </span>
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="number"
                            value={values.price_cents}
                            onChange={(e) => {
                              setEditValues({
                                ...editValues,
                                [config.id]: {
                                  ...values,
                                  price_cents: parseInt(e.target.value) || 0
                                }
                              });
                            }}
                            className="input-field w-24 py-1.5"
                            min="1"
                          />
                        ) : (
                          <span className="text-sm" style={{ color: 'var(--gp-ink)' }}>{config.price_cents}</span>
                        )}
                      </td>
                      <td>
                        <span className="text-sm font-semibold" style={{ color: 'var(--gp-ink)' }}>
                          {formatPrice(isEditing ? values.price_cents : config.price_cents)}
                        </span>
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            value={values.currency}
                            onChange={(e) => {
                              setEditValues({
                                ...editValues,
                                [config.id]: {
                                  ...values,
                                  currency: e.target.value
                                }
                              });
                            }}
                            className="gp-select min-w-0"
                          >
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="GBP">GBP</option>
                          </select>
                        ) : (
                          <span className="text-sm" style={{ color: 'var(--gp-ink)' }}>{config.currency}</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={values.is_active}
                              onChange={(e) => {
                                setEditValues({
                                  ...editValues,
                                  [config.id]: {
                                    ...values,
                                    is_active: e.target.checked
                                  }
                                });
                              }}
                            />
                            <span className="text-xs gp-text-muted">
                              {values.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        ) : (
                          <span
                            className="gp-badge text-[10px]"
                            style={
                              config.is_active
                                ? { background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }
                                : { background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)' }
                            }
                          >
                            {config.is_active ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td className="!text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleSave(config)}
                              disabled={saving[config.id]}
                              className="gp-btn-primary text-xs py-1.5 px-2.5 disabled:opacity-50"
                            >
                              {saving[config.id] ? (
                                <>
                                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" aria-hidden="true" />
                                  <span>Saving...</span>
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faSave} aria-hidden="true" />
                                  <span>Save</span>
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCancel(config.id)}
                              className="gp-btn-ghost text-xs py-1.5 px-2.5"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleEdit(config)}
                            className="gp-btn-primary text-xs py-1.5 px-2.5"
                          >
                            <FontAwesomeIcon icon={faEdit} aria-hidden="true" />
                            <span>Edit</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPricing;
